import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import type { SyntheticEvent } from 'react';
import React, { Component } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import ReactDOM from 'react-dom';
import { Manager, Popper, Reference } from 'react-popper';
import type { ApplyDefaultProps } from '../../types/utility';
import Trap from '../trap/Trap';
import styles from './Popover.module.scss';
import { PopoverTarget } from './PopoverTarget';
import { getAnimationPosition, isOpenCheck } from './utils';

export interface RawProps {
  /** Use position: fixed for this modal? Ideal if you want the popover "stuck to viewport" with a scrolling container  */
  fixed?: boolean;
  /** Which position to open the popover (top/right/bottom/left with -start and -end modifiers) */
  position: Placement;
  /** Set this to true to stop click-event from propagating, default=false! */
  stopPropagation: boolean;
  /** Should ESC close the dropdown? */
  enableKeyTrap: boolean;
  /** Should Popover close when clicking outside its container? */
  closeOnOutsideClick: boolean;
  /** Should Popover close when clicking inside its content? */
  closeOnClick: boolean;
  /** Should the popover open when prop.target recieves focus? */
  openOnTargetFocus: boolean;
  /** If target is <Button />, should caret be automatically applied? */
  targetCaret: boolean;
  /** When undefined (default), open/closer is handled in local state */
  isOpen: boolean | null | undefined;
  /** Let Popover manage it's own open state, but seed the initial state value */
  isInitiallyOpen?: boolean;
  /** Content of modal */
  children?: React.ReactNode | ((pos: Placement) => React.ReactElement);
  /** Target is the visible trigger to open/close this popover */
  target: React.ReactElement;
  className?: string; // Of the actual popper,
  /** Callback when popover opens */
  onOpen: (event: SyntheticEvent | Event) => void;
  /** Callback when popover closes */
  onClose: (event: SyntheticEvent | Event) => void;
  /** Wrap target in a container? Required for `targetWrapperElement` */
  targetWrapper: boolean;
  /** Element to wrap the target in */
  targetWrapperElement: 'span' | 'div';
  /** Variant theming */
  variant: 'default' | 'info' | 'muted';
  /** Flip popover when it can't expand to the intended position */
  flipEnabled: boolean;
  /** popper.js offset modifier `[skidding, distance]`; defaults to `[0, 4]` */
  offset?: [number, number];
  /** Modifier value: Prevents overflow/scrolling */
  preventOverflow: boolean;
  /** Custom modifiers (will overwrite other modifiers)
   * https://popper.js.org/popper-documentation.html#modifiers
   */
  /** Number of pixels to add to inner content */
  padding?: number;
  /** Max width of inner content */
  maxWidth: number | string | undefined;
  /** Default width of inner content */
  width: number | string | undefined;
  /** Max width of inner content */
  maxHeight: number | string | undefined;
  /** Default width of inner content */
  height: number | string | undefined;
  /** Force text to break that would potentially overflow the popover */
  preventTextOverflow: boolean;
  /** Use react-custom-scrollbars to manage overflowing popover content */
  customScrollbars: boolean;
  /** Specify z-index of Popover container */
  zIndex: number | undefined;
  /** false = prop.target onClick handler runs normally */
  ignoreTargetOnClick: boolean;
  /** `data-testid` attribute for the popover content when open */
  testId?: string;
  usePortal?: boolean;
}

export type PopoverProps = ApplyDefaultProps<RawProps, typeof Popover.defaultProps>;

interface State {
  isOpen: boolean;
}

/**
 * Popover Component is based off react-popper, powered by popper.js
 *
 * It usually handles itself pretty automatically, but pay attention to
 * the `targetWrapper` and `targetWrapperElement` options, as they are the
 * most relevant for most implementations.
 *
 * When using targetWrapper: false, you'll need to manage position: relative state yourself
 *
 * Defaults to 'auto' position
 *
 */
const flipBehavior: Array<Placement> = ['right', 'left', 'bottom', 'top'];

class Popover extends Component<RawProps, State> {
  static defaultProps = {
    fixed: false,
    target: null,
    position: 'auto',
    onOpen: () => null,
    onClose: () => null,
    isOpen: undefined,
    enableKeyTrap: true,
    closeOnOutsideClick: true,
    closeOnClick: false,
    openOnTargetFocus: false,
    stopPropagation: false,
    targetCaret: true,
    targetWrapper: true,
    // We default to div for legacy reasons
    targetWrapperElement: 'div',
    variant: 'default',
    // Modifier options that might be
    flipEnabled: true,
    offset: [0, 4] as [number, number],
    isInitiallyOpen: false,
    padding: 0,
    width: undefined,
    maxWidth: undefined,
    preventTextOverflow: false,
    maxHeight: undefined,
    height: undefined,
    // Todo: This should probably be default, but make sure we
    // fix double-overflows like AccountSwitcherDropdown
    customScrollbars: false,
    preventOverflow: true,
    zIndex: undefined,
    // Should probably be false by default, but don't have time to check all the use cases atm
    ignoreTargetOnClick: true,
    usePortal: false,
  };
  shouldScheduleUpdate = false;
  targetRef: { current: null | Element } = React.createRef();

  constructor(props: RawProps) {
    super(props);
    this.state = {
      isOpen: !!props.isInitiallyOpen,
    };
  }

  close = () => {
    if (!isOpenCheck(this.props, this.state)) return;
    this.handleTogglePopover(new Event('click'));
  };

  handleTogglePopover = (event: SyntheticEvent | Event) => {
    const isOpenStateControllerViaProp = this.props.isOpen !== undefined;
    const isCurrentlyOpen = isOpenCheck(this.props, this.state);

    const { current } = this.targetRef;
    if (event.target instanceof Element && current && current.contains(event.target) === false) {
      event.preventDefault();
    }

    // Stop click-event from propagating
    if (this.props.stopPropagation) {
      event.stopPropagation();
    }

    if (!isOpenStateControllerViaProp) {
      this.setState({ isOpen: !isCurrentlyOpen });
    }

    // Notify parent component when we open/close
    if (isCurrentlyOpen) {
      this.props.onClose(event);
    } else {
      this.props.onOpen(event);
    }
  };

  renderTarget(ref?: React.Ref<any>) {
    return (
      <PopoverTarget
        ref={ref}
        isCurrentlyOpen={isOpenCheck(this.props, this.state)}
        targetCaret={this.props.targetCaret}
        targetWrapper={this.props.targetWrapper}
        targetWrapperElement={this.props.targetWrapperElement}
        onFocus={this.props.openOnTargetFocus ? this.handleTogglePopover : null}
        onClick={this.handleTogglePopover}
        ignoreTargetOnClick={this.props.ignoreTargetOnClick}
        target={this.props.target}
      />
    );
  }

  render() {
    const isOpen = isOpenCheck(this.props, this.state);

    const {
      enableKeyTrap,
      closeOnOutsideClick,
      closeOnClick,
      className,
      children: childNodeOrFunction,
      variant,
      width,
      maxWidth,
      maxHeight,
      height,
      padding,
      preventTextOverflow,
      customScrollbars,
      zIndex,
      usePortal,
    } = this.props;

    // When modal isn't open or ready to be shown, we _just_ render the target.
    // This is intended as an optimization (but hasn't been verified)
    // If this doesn't impact performance, consider always rendering <Manager>...
    if (!isOpen) {
      return this.renderTarget();
    }

    return (
      <Manager>
        <Reference>
          {({ ref }) => {
            return this.renderTarget(ref);
          }}
        </Reference>
        {!!isOpen && (
          <Popper
            strategy={this.props.fixed ? 'fixed' : 'absolute'}
            placement={this.props.position}
            modifiers={[
              // We disable the built-in gpuAcceleration so that
              // Popper.js will return us easy to interpolate values
              // (top, left instead of transform: translate3d)
              { name: 'computeStyles', options: { gpuAcceleration: false } },
              { name: 'preventOverflow' },
              { name: 'offset', options: { offset: this.props.offset ?? [0, 4] } },
              { name: 'flip', options: { fallbackPlacements: flipBehavior } },
            ]}
          >
            {({ ref, style, placement }) => {
              const isInitialized = !!placement;
              const realPlacement = placement || this.props.position;
              const popperContentClass = classNames(
                styles.popper,
                styles[`variant-${variant}`],
                {
                  [styles.isInitialized]: isInitialized,
                  [styles.preventTextOverflow]: preventTextOverflow,
                },
                className
              );
              const animations = isInitialized
                ? { transform: 'translate3d(0,0,0) scale(1)' }
                : { transform: `translate3d(${getAnimationPosition(realPlacement)}) scale(0.95)` };

              const children: React.ReactNode =
                typeof childNodeOrFunction === 'function'
                  ? childNodeOrFunction(realPlacement)
                  : childNodeOrFunction;

              // To pad scrollbars content properly, padding is separate from container
              const isTrapEnabled = enableKeyTrap || closeOnOutsideClick;
              const childDom = (
                <Trap
                  enabled={isTrapEnabled}
                  shouldExitOnEsc={enableKeyTrap}
                  shouldExitOnClickOutside={closeOnOutsideClick}
                  onExit={this.handleTogglePopover}
                  style={{ padding }}
                >
                  {children}
                </Trap>
              );

              const content = (
                <div
                  style={{
                    width,
                    maxWidth,
                    maxHeight,
                    height,
                    ...style,
                    ...animations,
                    zIndex,
                    willChange: `transform, box-shadow`,
                  }}
                  className={popperContentClass}
                  ref={ref}
                  data-placement={realPlacement}
                  onClick={closeOnClick ? this.handleTogglePopover : undefined}
                  data-testid={this.props.testId}
                >
                  {customScrollbars ? (
                    <Scrollbars
                      autoHeight
                      hideTracksWhenNotNeeded
                      autoHeightMin={0}
                      autoHeightMax={maxHeight || height || 'calc(100vh - 40px)'}
                    >
                      {childDom}
                    </Scrollbars>
                  ) : (
                    childDom
                  )}
                </div>
              );

              return usePortal ? ReactDOM.createPortal(content, document.body) : content;
            }}
          </Popper>
        )}
      </Manager>
    );
  }
}

export default Popover;
